import { useEntity } from '@uswitch/ev-utils/context/selectors'
import Markdown from 'markdown-to-jsx'
import React, { ReactNode } from 'react'
import { useQuery } from 'urql'

import { WifiIcon, ListIcon, SwapHorizontalIcon, TickIcon } from '~/assets/icons'
import CoolingOffPeriod from '~/components/cooling-off-period'
import { FragmentType, useFragment } from '~/gql'
import { DealProvider, Term } from '~/gql/graphql'
import PageContentFragment from '~/graphql/fragments/page-content-fields'
import PAGE_CONTENT_QUERY from '~/graphql/queries/page-content'
import { getProviderPageId, providerEeveeContent } from '~/provider-routes'
import captureException from '~/utils/capture-exception'

import { Sections } from './sections'
import styles from './styles.module.css'

const EeveeContent = ({ eeveeEntryId, name }: { eeveeEntryId: string; name: string }) => {
  const eeveeEntry = useEntity({ id: eeveeEntryId })

  const unorderedList = eeveeEntry ? Array.from(eeveeEntry?.contentTwo?.content[1]?.content) : []

  const whyChooseProviderContent = unorderedList.map((item: any) => ({
    icon: <TickIcon />,
    subHeader: item.content[0].content[0].value,
    body: [item.content[0].content[1].value]
  }))

  return (
    <>
      <h6 className={styles.header}>{`Why choose ${name}?`}</h6>
      <Sections sections={whyChooseProviderContent} border largeBody />
    </>
  )
}

const PageContent = ({ pageId, name }: { pageId: string; name: string }) => {
  const [{ error, data }] = useQuery({
    query: PAGE_CONTENT_QUERY,
    variables: { contentId: pageId },
    requestPolicy: 'cache-first'
  })

  const pageContent: FragmentType<typeof PageContentFragment> = data?.pageContent ?? {}
  const content = useFragment(PageContentFragment, pageContent)

  if (error) {
    captureException(`Error in static data query: ${error}`)
    throw new Error(`Static data missing: ${error}`)
  }

  const { headerText } = content

  const Paragraph = ({ children, ...props }: { children: ReactNode; props: string }) => (
    <div className={styles.sectionContainer} data-is-markdown={true}>
      <TickIcon />
      <p {...props}>{children}</p>
    </div>
  )

  return (
    <div>
      {headerText && (
        <>
          <h6 className={styles.header}>{`Why choose ${name}?`}</h6>
          <Markdown
            options={{
              overrides: {
                div: { props: { className: styles.sections, 'data-has-border': true } },
                strong: { props: { className: styles.sectionSubHeader } },
                p: {
                  component: Paragraph,
                  props: { className: styles.sectionBody, 'data-large-body': true }
                }
              }
            }}
          >
            {headerText}
          </Markdown>
        </>
      )}
    </div>
  )
}

const CoolingOffPeriodWrapper = ({ hasBottomBorder = true }: { hasBottomBorder?: boolean }) => (
  <div className={styles.sections} data-has-border={hasBottomBorder}>
    <CoolingOffPeriod />
  </div>
)

const TermsAndConditions = ({ terms }: { terms: Term[] }) => {
  if (!terms.length) return null

  return (
    <>
      <h6 className={styles.header} data-medium-margin>
        Terms & conditions
      </h6>

      <div className={styles.sections}>
        {terms.map(({ detail }, i) => (
          <p className={styles.sectionBody} key={i}>
            {detail}
          </p>
        ))}
      </div>
    </>
  )
}

const ProviderContent = ({ provider, terms }: { provider: DealProvider; terms: Term[] }) => {
  const { name, id } = provider

  const eeveeEntryId = providerEeveeContent[id as keyof typeof providerEeveeContent]
  const pageId = getProviderPageId(id)

  const switchingContent = [
    {
      icon: <ListIcon />,
      header: 'Select your new deal',
      body: ['Choose your package and confirm your details.']
    },
    {
      icon: <SwapHorizontalIcon />,
      header: 'Check on your switch',
      body: [
        `Your new provider usually manages your switch. If you're changing network, check if you need to cancel your old service.`
      ]
    },
    {
      icon: <WifiIcon height='24px' width='24px' color='#141414' />,
      header: 'Get connected',
      body: [`${name} will get you online on your activation date. Keep an eye out for any updates.`]
    }
  ]

  return (
    <>
      {eeveeEntryId && <EeveeContent eeveeEntryId={eeveeEntryId} name={name} />}
      {!eeveeEntryId && pageId && <PageContent pageId={pageId} name={name} />}

      <h6 className={styles.header}>{`Switching to ${name}`}</h6>
      <Sections sections={switchingContent} largeBody noMargin />

      <CoolingOffPeriodWrapper hasBottomBorder={!!terms.length} />

      <TermsAndConditions terms={terms} />
    </>
  )
}

export default ProviderContent
